import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Info } from 'lucide-react';
import { TextInput } from '@/components/form/TextInput.tsx';
import { PromoCodeInput } from '@/components/form/PromoCodeInput.tsx';
import { NumberInput } from '@/components/form/NumberInput.tsx';
import { DateInput } from '@/components/form/DateInput.tsx';
import SingleSelectInput from '@/components/form/SingleSelectInput.tsx';
import ToggleInput from '@/components/form/ToggleInput.tsx';
import CheckboxInput from '@/components/form/CheckboxInput.tsx';
import { ValueInput } from '@/components/form/ValueInput.tsx';
import TwoInputsRow from '@/components/form/ui/TwoInputsRow.tsx';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import { SmailTitle } from '@/components/ui/Title.tsx';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/Tooltip.tsx';
import TourMultiSearchSelectRHF from '@/components/asyncSelect/entitySelects/TourMultiSearchSelectRHF.tsx';
import { DiscountInputData } from '@/features/dashboard/discounts/schemas/discountSchema.ts';
import {
  DiscountType,
  getDiscountTypeOptions,
} from '@/features/dashboard/discounts/types/DiscountType.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { currencySelectOptions } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import PrimitiveCheckbox from '@/components/form/_primitives/PrimitiveCheckbox.tsx';

const discountTypeLabels: Record<string, string> = {
  [DiscountType.PERCENTAGE]: 'Procentowy',
  [DiscountType.FIXED_AMOUNT]: 'Kwotowy',
};

const DiscountForm = ({ isEdit = false }: { isEdit?: boolean }) => {
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<DiscountInputData>();

  const type = watch('type');
  const value = watch('value');
  const currency = watch('currency') as string | undefined;
  const promoCode = watch('promoCode') as string;
  const minTotalPrice = watch('minTotalPrice');

  const [autoApply, setAutoApply] = useState(() => isEdit && !promoCode);

  useEffect(() => {
    if (autoApply) {
      setValue('promoCode', '');
    }
  }, [autoApply, setValue]);

  useEffect(() => {
    if (type !== DiscountType.FIXED_AMOUNT) return;
    const numValue = Number(value);
    if (!numValue || isNaN(numValue)) return;
    if (
      minTotalPrice === null ||
      (typeof minTotalPrice === 'number' && minTotalPrice < numValue)
    ) {
      setValue('minTotalPrice', numValue, { shouldValidate: true });
    }
  }, [type, value, minTotalPrice, setValue]);

  return (
    <>
      <TextInput
        label="Nazwa"
        required
        placeholder="Np. Early bird -200 PLN"
        register={register('name')}
        error={errors.name?.message}
      />

      {isEdit ? (
        <div className="flex flex-col gap-1.5">
          <InputLabel label="Typ rabatu" />
          <span className="text-sm text-neutral-600">
            {discountTypeLabels[type] ?? type}
          </span>
        </div>
      ) : (
        <ToggleInput
          fieldPath="type"
          label="Typ rabatu"
          required
          options={getDiscountTypeOptions()}
          control={control}
          error={errors.type?.message}
          onChange={() => {
            setValue('currency', Currency.PLN);
          }}
        />
      )}

      <TwoInputsRow ratio="2/1">
        <ValueInput
          label="Wartość"
          register={register('value', { valueAsNumber: true })}
          placeholder="0"
          required
          type="number"
          step={0.01}
          error={errors.value?.message}
          sufix={type === DiscountType.PERCENTAGE ? '%' : undefined}
        />
        <SingleSelectInput
          fieldName="currency"
          control={control}
          options={currencySelectOptions}
          label="Waluta"
          required
          error={errors.currency?.message}
        />
      </TwoInputsRow>

      <div>
        <div className="flex items-center gap-2">
          <PrimitiveCheckbox
            checked={autoApply}
            onChange={(e) => setAutoApply(e.target.checked)}
          />
          <label
            className="cursor-pointer text-sm font-medium"
            onClick={() => setAutoApply((prev) => !prev)}
          >
            Automatyczna aktywacja
          </label>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info size={14} className="text-neutral-400" />
            </TooltipTrigger>
            <TooltipContent>
              Rabat zostanie automatycznie zastosowany, jeśli wszystkie
              ustawione warunki zostaną spełnione. Klient nie musi wpisywać
              kodu.
            </TooltipContent>
          </Tooltip>
        </div>
        {!autoApply && (
          <div className="mt-3">
            <PromoCodeInput
              register={register('promoCode')}
              setValue={setValue}
              fieldName="promoCode"
              error={errors.promoCode?.message}
              hint="Min. 3 znaki, maks. 100 znaków. Zalecana długość: 12+ znaków."
            />
          </div>
        )}
      </div>

      {isEdit && (
        <CheckboxInput
          label="Aktywna"
          register={register('active')}
          error={errors.active?.message}
        />
      )}

      <div className="border-t border-neutral-200 pt-4">
        <SmailTitle>Konfiguracja szczegółowa</SmailTitle>
      </div>

      <TwoInputsRow>
        <DateInput
          dateFieldName="validFrom"
          control={control}
          label="Ważny od"
          error={errors.validFrom?.message}
        />
        <DateInput
          dateFieldName="validTo"
          control={control}
          label="Ważny do"
          error={errors.validTo?.message}
        />
      </TwoInputsRow>

      <TwoInputsRow>
        <NumberInput
          label="Min. uczestników"
          register={register('minParticipants', { valueAsNumber: true })}
          placeholder="Brak limitu"
          error={errors.minParticipants?.message}
          min={1}
          step={1}
        />
        <ValueInput
          label="Min. wartość zamówienia"
          register={register('minTotalPrice', { valueAsNumber: true })}
          placeholder="Brak limitu"
          type="number"
          step={0.01}
          error={errors.minTotalPrice?.message}
          sufix={currency}
        />
      </TwoInputsRow>

      <NumberInput
        label="Maks. użyć kodu"
        register={register('maxUsages', { valueAsNumber: true })}
        placeholder="Brak limitu"
        error={errors.maxUsages?.message}
        min={1}
        step={1}
      />

      {type === DiscountType.PERCENTAGE && (
        <ValueInput
          label="Maks. kwota rabatu"
          register={register('maxDiscountAmount', { valueAsNumber: true })}
          placeholder="Brak limitu"
          type="number"
          step={0.01}
          error={errors.maxDiscountAmount?.message}
          sufix={currency}
        />
      )}

      <CheckboxInput
        label="Łączy się z innymi rabatami"
        register={register('combinable')}
        error={errors.combinable?.message}
      />

      <TourMultiSearchSelectRHF
        fieldPathName="tourIds"
        control={control}
        label="Działa tylko dla wybranych wyjazdów"
        error={errors.tourIds?.message}
        placeholder="Wyszukaj wyjazdy..."
      />
    </>
  );
};

export default DiscountForm;
